import React, { useMemo } from "react";
import { Box, Button, Chip, LinearProgress, Typography } from "@mui/material";

import { useCopilotUsageCapabilities } from "components/RequirePermission/useCapabilities";
import CopilotIcon from "components/shared/CopilotIcon/CopilotIcon";
import { getBudgetBarColor, getBudgetTextColor } from "pages/admin/components/CopilotDashboard/budgetColors";
import { getJoinedOrganizations, getLicensedOrgIds, UserCostBillingCard } from "pages/admin/components/UserDetailsModal/UserProfileSections";
import { useGetCopilotUserDetailQuery } from "state/rtkQuery/copilot/copilotQueries";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetCostCenterListQuery, useGetUserQuery } from "state/rtkQuery/user/userQueries";
import { formatLocalDateTime } from "utils/formatLocalDateTime";

import { ACCENT, Card, EmptyNote } from "./MeteredParts";
import { formatMoney } from "./meteredFormat";

const RED = "#E2231A";

const BudgetMetaDot = () => (
  <Typography component="span" aria-hidden="true" sx={{ color: "#ccc", fontSize: "0.9rem", lineHeight: 1 }}>
    ·
  </Typography>
);

const ErrorWithRetry = ({ children, onRetry }) => (
  <EmptyNote>
    {children}{" "}
    <Button size="small" onClick={onRetry} sx={{ textTransform: "none", color: ACCENT, fontStyle: "normal" }}>
      Retry
    </Button>
  </EmptyNote>
);

// Who this person is, from the same user record the Copilot dashboard shows: their cost and billing profile (GitHub
// cost center, business name / area / line, RC leader, GPS) and the organizations they've joined.
export const PersonProfileCard = ({ email, userId, platformId, range }) => {
  const { data, isFetching, isError, error, refetch } = useGetUserQuery({ email, platformId }, { skip: !email || !platformId });
  const { data: orgs } = useGetActiveOrganizationsQuery(undefined, { skip: !email });
  const { data: costCenterList } = useGetCostCenterListQuery({ platformId, state: "active" }, { skip: !platformId });
  const orgIdToName = useMemo(() => Object.fromEntries((orgs ?? []).map((org) => [org._id, org.name])), [orgs]);
  const costCenterById = useMemo(() => Object.fromEntries((costCenterList ?? []).map((cc) => [cc._id, cc.name])), [costCenterList]);

  const users = Array.isArray(data) ? data : [];
  const user = users.find((u) => u._id === userId) ?? users[0] ?? null;
  const joined = getJoinedOrganizations(user, platformId);
  const licensed = getLicensedOrgIds(user);
  const forbidden = error?.status === 403;

  return (
    <Card title="Profile" subtitle={email ?? "Waiting for the person's details"} from={range.from} to={range.to} hideDate loading={isFetching} isInitialLoading={isFetching && !data} skeleton="list" skeletonRows={3}>
      {!email ? (
        <EmptyNote>No email on record for this person, so their profile can't be looked up.</EmptyNote>
      ) : isError ? (
        forbidden ? (
          <EmptyNote>You don't have permission to view this person's profile.</EmptyNote>
        ) : (
          <ErrorWithRetry onRetry={refetch}>Couldn't load this person's profile.</ErrorWithRetry>
        )
      ) : !user ? (
        <EmptyNote>Could not find user details for this person.</EmptyNote>
      ) : (
        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }} data-testid="person-profile">
          <UserCostBillingCard compact user={user} platformId={platformId} costCenterById={costCenterById} />
          {joined.length > 0 ? (
            <Box>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#0b756c", letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.75 }}>Where they've joined</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {joined.slice(0, 8).map((org) => (
                  <Box key={org.id} data-testid="joined-org" sx={{ fontSize: "0.75rem", color: "#1e293b", backgroundColor: "#f6f8fa", border: "1px solid #e8edf2", borderRadius: "12px", px: 1.1, py: 0.2 }}>
                    {orgIdToName[org.id] || org.id}
                    {licensed.has(org.id) && " · Copilot"}
                  </Box>
                ))}
                {joined.length > 8 && <Typography sx={{ fontSize: "0.75rem", color: "#888", alignSelf: "center" }}>+{joined.length - 8} more</Typography>}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.8rem", color: "#bbb", fontStyle: "italic" }}>No joined organizations on this platform.</Typography>
          )}
        </Box>
      )}
    </Card>
  );
};

// The person's Copilot budget — how much of it they've consumed — from the Copilot per-user usage call, exactly as on
// the Copilot dashboard. That call is per organization, so this is only for the organization page, and it needs the
// permission to read a person's Copilot usage. A person with no Copilot usage in the period has no budget to show.
export const PersonBudgetCard = ({ email, orgId, platformId, range }) => {
  const { canViewUsageById } = useCopilotUsageCapabilities(orgId);
  const { data, isFetching, isError, error, refetch } = useGetCopilotUserDetailQuery(
    { email, orgId, platformId, fromDate: range.from, toDate: range.to },
    { skip: !email || !orgId || !platformId || !canViewUsageById }
  );
  if (!canViewUsageById) return null;

  const budget = data?.email === email ? data?.budget : null;
  const noUsage = error?.status === 404;
  const pct = budget && budget.budget_amount > 0 ? Math.min((budget.consumed_amount / budget.budget_amount) * 100, 100) : 0;
  const over = budget && budget.consumed_amount > budget.budget_amount;
  const barColor = getBudgetBarColor(pct);

  return (
    <Card title="Copilot Budget" subtitle="How much of their Copilot budget this person has used" from={range.from} to={range.to} hideDate loading={isFetching} isInitialLoading={isFetching && !data} skeleton="list" skeletonRows={3}>
      {isError && !noUsage ? (
        <ErrorWithRetry onRetry={refetch}>Couldn't load this person's budget.</ErrorWithRetry>
      ) : noUsage ? (
        <EmptyNote>No Copilot usage in this period, so there are no budget details to show.</EmptyNote>
      ) : (
        <Box sx={{ p: 2.5 }} data-testid="person-budget">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <CopilotIcon aria-label="Copilot" sx={{ fontSize: 16, color: "#1a1a1a" }} />
            {!budget && <Chip label="NOT SET" size="small" sx={{ fontSize: "0.68rem", fontWeight: 700, height: 20, backgroundColor: "#f0f0f0", color: "#999" }} />}
            {budget?.updated_at && (
              <>
                <BudgetMetaDot />
                <Typography sx={{ fontSize: "0.76rem", color: "#777" }}>
                  Updated <Box component="span" sx={{ fontWeight: 700, color: "#1a1a1a" }}>{formatLocalDateTime(budget.updated_at)}</Box>
                </Typography>
              </>
            )}
            {budget && budget.last_activity_at !== undefined && (
              <>
                <BudgetMetaDot />
                <Typography sx={{ fontSize: "0.76rem", color: "#777" }}>
                  Last Copilot activity{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#1a1a1a" }}>{budget.last_activity_at ? formatLocalDateTime(budget.last_activity_at) : "No activity recorded"}</Box>
                </Typography>
              </>
            )}
          </Box>
          {budget ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.7 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>
                  {formatMoney(budget.consumed_amount)} consumed of {formatMoney(budget.budget_amount)}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: getBudgetTextColor(barColor) }}>{pct.toFixed(1)}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct} aria-label="Budget used" sx={{ height: 8, borderRadius: 8, backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { backgroundColor: barColor, borderRadius: 8 } }} />
              <Typography sx={{ fontSize: "0.72rem", color: over ? RED : "#aaa", fontWeight: over ? 600 : 400, mt: 0.8 }}>
                {over ? `Over budget by ${formatMoney(budget.consumed_amount - budget.budget_amount)}` : `${formatMoney(budget.budget_amount - budget.consumed_amount)} remaining`}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.8rem", color: "#bbb", fontStyle: "italic" }}>No budget has been configured for this user.</Typography>
          )}
        </Box>
      )}
    </Card>
  );
};
